/*
 * agb-gallery-color-sync.js  (isolated, additive — touches NOTHING upstream)
 * ---------------------------------------------------------------------------
 * Problem it works around: on the AGB gear-builder PDP, the Scarcity Engine
 * re-renders the colour swatches on every variant change, which detaches the
 * variant-picker's per-swatch click handlers. So the 2nd+ colour click never
 * registers a real selection and the product gallery snaps back to the default
 * variant's image.
 *
 * This script bypasses that broken variant:change -> gallery path entirely.
 * The side thumbnails already switch the main image correctly (with the slide
 * animation). So: whenever a colour swatch is clicked, we resolve the clicked
 * colour -> its variant -> variant.featured_media.id, and CLICK the matching
 * side thumbnail. The shopper sees the same result as clicking the thumbnail.
 *
 * Why this is safe / isolated:
 *  - One DELEGATED listener on `document` (capture phase). It survives every
 *    swatch re-render the engine does (it's not bound to a swatch).
 *  - Reads the clicked colour DIRECTLY off the clicked swatch element, so it
 *    does not depend on the (stale/lagging) option inputs the bug corrupts.
 *  - Reads variant data from window.ShopifyProduct (a global) only.
 *  - Drives the gallery by clicking a thumbnail — exactly what a shopper does.
 *  - Does NOT modify the variant picker, the Scarcity Engine, or any dispatch.
 */
(function () {
  'use strict';

  // --- helpers ------------------------------------------------------------

  function selectedOptionValues(picker) {
    var out = {};
    try {
      picker.querySelectorAll('input[name^="option"]').forEach(function (inp) {
        if (inp.value) out[inp.name] = inp.value;
      });
    } catch (_) {}
    return out;
  }

  // Resolve the variant for the clicked colour (+ whatever other options are
  // currently selected). The clicked value WINS over the (possibly stale) input
  // for its own option. Falls back to "any available variant of this colour".
  function resolveVariant(product, clickedName, clickedValue, sel) {
    var variants = (product && product.variants) || [];
    if (!variants.length) return null;

    var opts = {};
    Object.keys(sel || {}).forEach(function (k) { opts[k] = sel[k]; });
    if (clickedName) opts[clickedName] = clickedValue;
    var names = Object.keys(opts);

    var exact = variants.find(function (v) {
      return names.every(function (n) {
        var i = parseInt(n.replace('option', ''), 10) - 1;
        return String((v.options || [])[i]) === String(opts[n]);
      });
    });
    if (exact) return exact;

    // Fallback: first available (else any) variant whose options include the
    // clicked colour value. Good enough for the gallery image (featured_media
    // is per-colour, shared across sizes).
    return (
      variants.find(function (v) {
        return v.available && (v.options || []).some(function (o) { return String(o) === String(clickedValue); });
      }) ||
      variants.find(function (v) {
        return (v.options || []).some(function (o) { return String(o) === String(clickedValue); });
      }) ||
      null
    );
  }

  function variantMediaId(variant) {
    if (!variant) return null;
    if (variant.featured_media && variant.featured_media.id != null) return variant.featured_media.id;
    if (variant.featured_image && variant.featured_image.id != null) return variant.featured_image.id;
    return null;
  }

  // Product data source — portable across stores: window.ShopifyProduct (some sections set it)
  // OR the #product-data JSON the AGB section always emits OR window.AGB_BOOT.productJson.
  function getProduct() {
    if (window.ShopifyProduct && window.ShopifyProduct.variants) return window.ShopifyProduct;
    try {
      var el = document.getElementById('product-data');
      if (el) { var p = JSON.parse(el.textContent || '{}'); if (p && p.variants) return p; }
    } catch (_) {}
    var b = window.AGB_BOOT && window.AGB_BOOT.productJson;
    if (b && b.variants) return b;
    return null;
  }

  function numericTail(id) { return String(id).replace(/^.*\//, ''); }

  function galleryIsVisible() {
    // In Step 2 (after accessories) the AGB canvas replaces the gallery; don't
    // fight the canvas there. offsetParent is null when display:none.
    var g = document.querySelector('.product-gallery');
    return !!(g && g.offsetParent !== null);
  }

  function findThumbnail(mediaId) {
    if (mediaId == null) return null;
    var direct = document.querySelector('.product-gallery__thumbnail[data-media-id="' + mediaId + '"]');
    if (direct) return direct;
    // tolerate gid:// vs numeric differences by matching the numeric tail
    var want = numericTail(mediaId);
    var all = document.querySelectorAll('.product-gallery__thumbnail[data-media-id]');
    for (var i = 0; i < all.length; i++) {
      if (numericTail(all[i].getAttribute('data-media-id')) === want) return all[i];
    }
    return null;
  }

  function showMedia(mediaId) {
    if (mediaId == null || !galleryIsVisible()) return;
    var want = numericTail(mediaId);

    // Preferred: the theme's <scroll-carousel> INSTANT select — select(index, {instant:true})
    // scrolls with behavior:'auto' (no slide animation), per the request to drop the animation
    // for colour-driven switches. `cells` is the visible-cell list in select()'s own index space.
    var carousel = document.querySelector('.product-gallery__carousel');
    if (carousel && typeof carousel.select === 'function' && carousel.cells) {
      var cells = carousel.cells;
      for (var i = 0; i < cells.length; i++) {
        var id = cells[i] && cells[i].getAttribute && cells[i].getAttribute('data-media-id');
        if (id && numericTail(id) === want) {
          try {
            if (carousel.selectedIndex === i) return; // already showing it
            carousel.select(i, { instant: true });
            return;
          } catch (_) { break; }
        }
      }
    }

    // Fallback (target media filtered/hidden, or carousel API unavailable): click the
    // thumbnail — animated, but behaves exactly like a manual thumbnail click.
    var thumb = findThumbnail(mediaId);
    if (thumb && thumb.getAttribute('aria-current') !== 'true') thumb.click();
  }

  // --- the one delegated listener ----------------------------------------

  var generation = 0; // so a newer colour click supersedes older re-assert passes

  document.addEventListener('click', function (e) {
    var swatch = e.target.closest && e.target.closest('[data-option-value]');
    if (!swatch) return;
    var picker = swatch.closest('variant-picker');
    if (!picker) return;

    var input = swatch.querySelector('input') || swatch.previousElementSibling;
    var clickedName = input && input.name;
    var clickedValue =
      swatch.getAttribute('value') ||
      (input && input.value) ||
      (swatch.textContent || '').trim();
    if (!clickedValue) return;

    var product = getProduct();
    if (!product) return;

    var myGen = ++generation;

    // Re-assert across a short window: the first pass usually wins, but if the
    // engine's churn snaps the gallery back to default we correct it again.
    // aria-current check makes redundant passes no-ops (no extra animation),
    // and the generation check stops a stale colour from overriding a newer one.
    [0, 90, 220, 450].forEach(function (delay) {
      setTimeout(function () {
        if (myGen !== generation) return;
        try {
          var variant = resolveVariant(product, clickedName, clickedValue, selectedOptionValues(picker));
          showMedia(variantMediaId(variant));
        } catch (_) {}
        // Heal the AGB store's base item too. The same engine churn that
        // breaks the gallery ALSO leaves window.baseItem on the PREVIOUS
        // colour (verified 2026-07-13 on the belt PDP: UI + form said Black
        // while baseItem stayed Ranger Green) — and the gear-builder
        // Add-to-Cart prefers baseItem.id, so the shopper would get the
        // WRONG colour. syncBaseItemVariant() is the engine's own public
        // resync (reads the form's hidden [name="id"], which Prestige keeps
        // correct, and mutates the store + repaints). Idempotent; no-ops on
        // step-1 pages (no baseItem yet) and non-AGB pages (no module).
        try {
          var V = window.AGB && window.AGB.require && window.AGB.require('variant');
          if (V && typeof V.syncBaseItemVariant === 'function') V.syncBaseItemVariant();
        } catch (_) {}
      }, delay);
    });
  }, true);
})();
